// Transfer history list with basic + advanced search, shown under the transfer form.
// Click a row to open a detail modal listing every device + its IMEIs.
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, SlidersHorizontal, X, ArrowRight, Package } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { formatDateTime } from "@/lib/format/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { CategoryMultiSelect } from "@/features/categories/category-multi-select";
import { SerialHistoryModal } from "./serial-history-modal";

interface Warehouse {
  id: string;
  name: string;
}

interface TransferItem {
  sku: string;
  productName: string;
  quantity: number;
  serialCount: number;
  serials: string[];
}

interface TransferRow {
  transferId: string;
  createdAt: string;
  fromWarehouse?: string | null;
  toWarehouse?: string | null;
  performedBy?: string | null;
  note?: string | null;
  lineCount: number;
  totalQty: number;
  items: TransferItem[];
}

interface TransferResponse {
  data: TransferRow[];
  meta: { total: number; limit: number; offset: number };
}

/** Debounce a rapidly-changing value so typing doesn't fire a request per keystroke. */
function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function fmtDate(s: string) {
  return formatDateTime(s);
}

// Stable, readable transfer code derived from the transfer's date + id suffix.
// Transfers have no stored code (they are grouped inventory_transactions); this
// gives staff a consistent "CKyymmdd-XXXX" label to reference a phiếu by.
function transferCode(t: TransferRow) {
  const d = new Date(t.createdAt);
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const suffix = t.transferId.replace(/-/g, "").slice(0, 4).toUpperCase();
  return `CK${yy}${mm}${dd}-${suffix}`;
}

// Note is stored as "Transfer <from> → <to>[: <userNote>]" — show only the user part.
function cleanNote(note?: string | null) {
  if (!note) return "";
  if (note.startsWith("Transfer ")) {
    const idx = note.indexOf(": ");
    return idx >= 0 ? note.slice(idx + 2) : "";
  }
  return note;
}

export function TransferHistory({ warehouses }: { warehouses: Warehouse[] }) {
  const [q, setQ] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fromWh, setFromWh] = useState("");
  const [toWh, setToWh] = useState("");
  const [product, setProduct] = useState("");
  const [imei, setImei] = useState("");
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<TransferRow | null>(null);
  const [serialView, setSerialView] = useState<string | null>(null);
  const limit = 20;

  const dq = useDebounced(q);
  const dProduct = useDebounced(product);
  const dImei = useDebounced(imei);

  const hasAdvanced = !!(fromWh || toWh || dProduct || dImei || categoryIds.length || dateFrom || dateTo);

  useEffect(() => {
    setPage(0);
  }, [dq, fromWh, toWh, dProduct, dImei, categoryIds, dateFrom, dateTo]);

  const params: Record<string, string | number> = { limit, offset: page * limit };
  if (dq) params.q = dq;
  if (fromWh) params.from_warehouse_id = fromWh;
  if (toWh) params.to_warehouse_id = toWh;
  if (dProduct) params.product = dProduct;
  if (dImei) params.imei = dImei;
  if (categoryIds.length) params.category_ids = categoryIds.join(",");
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;

  const { data, isFetching } = useQuery({
    queryKey: ["transfers", params],
    queryFn: () => apiClient.get<TransferResponse>("/v1/inventory/transfers", { params }).then((r) => r.data),
    placeholderData: (prev) => prev,
  });

  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  function clearAll() {
    setQ("");
    setFromWh("");
    setToWh("");
    setProduct("");
    setImei("");
    setCategoryIds([]);
    setDateFrom("");
    setDateTo("");
  }

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Lịch sử chuyển kho</h2>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1 max-w-sm">
          <Search size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tìm theo sản phẩm, SKU, IMEI, ghi chú…"
            className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <Button
          variant={showAdvanced ? "default" : "outline"}
          size="sm"
          onClick={() => setShowAdvanced((v) => !v)}
          className="gap-1.5"
        >
          <SlidersHorizontal size={15} />
          Tìm nâng cao
          {hasAdvanced && <span className="ml-1 rounded-full bg-primary-foreground/20 px-1.5 text-xs">•</span>}
        </Button>
        {(q || hasAdvanced) && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="gap-1 text-muted-foreground">
            <X size={14} /> Xóa lọc
          </Button>
        )}
      </div>

      {/* Advanced filters */}
      {showAdvanced && (
        <div className="grid grid-cols-1 gap-3 rounded-lg border bg-muted/30 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Kho nguồn</span>
            <select
              value={fromWh}
              onChange={(e) => setFromWh(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Tất cả</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Kho đích</span>
            <select
              value={toWh}
              onChange={(e) => setToWh(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Tất cả</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Nhóm sản phẩm</span>
            <CategoryMultiSelect values={categoryIds} onChange={setCategoryIds} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Sản phẩm (tên / SKU)</span>
            <Input value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Tên hoặc mã SKU…" />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">IMEI / Serial</span>
            <Input value={imei} onChange={(e) => setImei(e.target.value)} placeholder="Nhập IMEI…" className="font-mono" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Từ ngày</span>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Đến ngày</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </label>
          </div>
        </div>
      )}

      <Table>
        <THead>
          <Tr>
            <Th>Mã phiếu</Th>
            <Th>Thời gian</Th>
            <Th>Kho nguồn → đích</Th>
            <Th className="text-right">Mặt hàng</Th>
            <Th className="text-right">SL</Th>
            <Th>Người thực hiện</Th>
            <Th>Ghi chú</Th>
          </Tr>
        </THead>
        <TBody>
          {isFetching && rows.length === 0 && (
            <Tr><Td colSpan={7} className="text-center text-muted-foreground">Đang tải…</Td></Tr>
          )}
          {!isFetching && rows.length === 0 && (
            <Tr><Td colSpan={7} className="text-center text-muted-foreground">Chưa có phiếu chuyển kho</Td></Tr>
          )}
          {rows.map((t) => (
            <Tr key={t.transferId} className="cursor-pointer hover:bg-muted/40" onClick={() => setSelected(t)}>
              <Td className="font-mono text-xs font-medium text-primary whitespace-nowrap">{transferCode(t)}</Td>
              <Td className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(t.createdAt)}</Td>
              <Td className="text-sm">
                <span className="inline-flex items-center gap-1.5">
                  <span className="font-medium">{t.fromWarehouse ?? "—"}</span>
                  <ArrowRight size={13} className="text-muted-foreground" />
                  <span className="font-medium">{t.toWarehouse ?? "—"}</span>
                </span>
              </Td>
              <Td className="text-right">{t.lineCount}</Td>
              <Td className="text-right font-semibold">{t.totalQty}</Td>
              <Td className="text-sm text-muted-foreground">{t.performedBy ?? "—"}</Td>
              <Td className="max-w-[220px] truncate text-sm text-muted-foreground" title={cleanNote(t.note)}>
                {cleanNote(t.note) || "—"}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center gap-3 text-sm">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Trước</Button>
          <span className="text-muted-foreground">
            {total} phiếu — trang {page + 1}/{totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Sau</Button>
        </div>
      )}

      {selected && (
        <TransferDetailModal
          transfer={selected}
          onClose={() => setSelected(null)}
          onOpenSerial={(sn) => setSerialView(sn)}
        />
      )}
      {serialView && <SerialHistoryModal serialNo={serialView} onClose={() => setSerialView(null)} />}
    </div>
  );
}

// TransferDetailModal shows a single transfer's full contents: every line item
// with its per-device IMEIs (clickable to open that serial's history).
function TransferDetailModal({
  transfer,
  onClose,
  onOpenSerial,
}: {
  transfer: TransferRow;
  onClose: () => void;
  onOpenSerial: (serialNo: string) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl border bg-background p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Chi tiết phiếu chuyển kho</h2>
            <p className="font-mono text-sm text-primary">{transferCode(transfer)}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X size={18} /></Button>
        </div>

        {/* Meta */}
        <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg border bg-muted/30 p-3 text-sm sm:grid-cols-2">
          <div className="flex justify-between gap-2 sm:justify-start sm:gap-2">
            <span className="text-muted-foreground">Thời gian:</span>
            <span className="font-medium">{fmtDate(transfer.createdAt)}</span>
          </div>
          <div className="flex justify-between gap-2 sm:justify-start">
            <span className="text-muted-foreground">Người thực hiện:</span>
            <span className="font-medium">{transfer.performedBy ?? "—"}</span>
          </div>
          <div className="flex items-center gap-1.5 sm:col-span-2">
            <span className="text-muted-foreground">Tuyến:</span>
            <span className="font-medium">{transfer.fromWarehouse ?? "—"}</span>
            <ArrowRight size={13} className="text-muted-foreground" />
            <span className="font-medium">{transfer.toWarehouse ?? "—"}</span>
          </div>
          <div className="flex justify-between gap-2 sm:justify-start">
            <span className="text-muted-foreground">Số mặt hàng:</span>
            <span className="font-medium">{transfer.lineCount}</span>
          </div>
          <div className="flex justify-between gap-2 sm:justify-start">
            <span className="text-muted-foreground">Tổng SL:</span>
            <span className="font-medium">{transfer.totalQty}</span>
          </div>
          {cleanNote(transfer.note) && (
            <div className="flex gap-2 sm:col-span-2">
              <span className="text-muted-foreground">Ghi chú:</span>
              <span className="font-medium">{cleanNote(transfer.note)}</span>
            </div>
          )}
        </div>

        {/* Items with IMEIs */}
        <div className="space-y-3">
          {transfer.items.map((it, idx) => {
            const serials = it.serials ?? [];
            return (
            <div key={idx} className="rounded-lg border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 font-medium">
                    <Package size={14} className="shrink-0 text-muted-foreground" />
                    <span className="truncate">{it.productName}</span>
                  </div>
                  <div className="font-mono text-xs text-muted-foreground">{it.sku}</div>
                </div>
                <span className="shrink-0 whitespace-nowrap text-sm">SL: <strong>{it.quantity}</strong></span>
              </div>
              {serials.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {serials.map((sn) => (
                    <button
                      key={sn}
                      onClick={() => onOpenSerial(sn)}
                      className="rounded border border-input bg-muted/40 px-2 py-0.5 font-mono text-xs text-foreground hover:border-primary hover:text-primary"
                      title="Xem lịch sử IMEI"
                    >
                      {sn}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">Sản phẩm không quản lý theo IMEI/serial.</p>
              )}
            </div>
            );
          })}
          {transfer.items.length === 0 && (
            <p className="text-sm text-muted-foreground">Không có chi tiết mặt hàng.</p>
          )}
        </div>
      </div>
    </div>
  );
}
